import React from "react";
import ReactDOM from "react-dom/client";
import { initApp } from "@multiversx/sdk-dapp/out/methods/initApp/initApp";
import { EnvironmentsEnum } from "@multiversx/sdk-dapp/out/types/enums.types";
import App from "./App";
import ErrorBoundary from "./ErrorBoundary";
import { loadConfig, type AppConfig } from "./config";
import "./styles.css";

async function boot() {
  // Two-step boot:
  //   1. /api/config — gives us the contract address + network.
  //   2. Query the chain for the contract's on-chain owner; we never trust
  //      a config file to tell us who admin is.
  let cfg: AppConfig | null = null;
  let cfgErr: string | null = null;
  let contractOwner = "";
  try {
    cfg = await loadConfig();
    if (cfg.contractAddress) {
      const { getContractOwner } = await import("./escrow");
      contractOwner = await getContractOwner(cfg);
    }
  } catch (e) {
    cfgErr = String(e);
  }

  const envKey = (cfg?.network ?? "testnet") as keyof typeof EnvironmentsEnum;
  await initApp({
    dAppConfig: {
      environment: EnvironmentsEnum[envKey] ?? EnvironmentsEnum.testnet,
      nativeAuth: false,
      // Wire WalletConnect / xPortal only if a project ID is configured —
      // otherwise the xPortal option in the picker shows as "configure to enable".
      ...(cfg?.walletConnectProjectId
        ? {
            providers: {
              walletConnect: {
                walletConnectV2ProjectId: cfg.walletConnectProjectId,
              },
            },
          }
        : {}),
    },
  });

  ReactDOM.createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
      <ErrorBoundary>
        <App initialConfig={cfg} initialError={cfgErr} contractOwner={contractOwner} />
      </ErrorBoundary>
    </React.StrictMode>,
  );
}

boot().catch((e) => {
  // Last-resort error display if even initApp blows up.
  document.getElementById("root")!.innerHTML =
    `<div style="font-family:sans-serif;color:#e6edf3;padding:2rem;background:#0e1116;height:100vh">
       <h2>Boot error</h2>
       <pre style="color:#f85149">${String(e)}</pre>
     </div>`;
});
